import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../auth/AuthProvider';
import type { SupabaseClient } from '../../lib/supabase';
import type { Database } from '../../types/database.types';
import type { Prospect } from './prospects';
import type { Tier1JourneyEvidence } from './prospectDmFunnel';
import { prospectKeys, PROSPECT_PAGE, prospectErrorMessage } from './prospects';

export type LeadJourney = Database['public']['Views']['lead_linkedin_journey']['Row'];
export type DealJourney = Database['public']['Views']['deal_linkedin_journey']['Row'];
export type WeeklyScore = Database['public']['Views']['linkedin_scoreboard_weekly']['Row'];
export type MessageScore = Database['public']['Views']['linkedin_scoreboard_by_message']['Row'];
export type LeadMilestone = 'call_held' | 'no_show' | 'proposal_sent';

const LEAD_COLUMNS =
  'lead_id, prospect_id, tier, prospect_source, batch_id, prospect_created_at, promoted_at, first_dm_at, last_dm_at, dm_count, first_dm_copy_set_id, first_dm_copy_set_name, replied_at, replied_copy_set_id, replied_copy_set_name, info_reply_at, overview_sent_at, call_booked_at, call_held_at, no_show_at, proposal_sent_at';
const DEAL_COLUMNS =
  'deal_id, lead_id, pipeline, outcome, closed_on, deal_created_at, cg_order_count, first_cg_order_id, first_cg_order_no, prospect_id, tier, prospect_source, batch_id, prospect_created_at, promoted_at, first_dm_at, dm_count, first_dm_copy_set_id, first_dm_copy_set_name, replied_at, info_reply_at, overview_sent_at, call_booked_at, call_held_at, no_show_at, proposal_sent_at';
const WEEK_COLUMNS =
  'week_start, tier, actor_id, dms_sent, people_messaged, replies, info_replies, overviews_sent, promoted_to_lead, calls_booked, calls_held, no_shows, proposals_sent, deals_created, deals_won';
const MESSAGE_COLUMNS =
  'tier, copy_set_id, copy_set_name, archived_at, people_messaged, replied, info_replied, calls_booked';
// A prospect tier correction invalidates these reads as well as Prospecting.
export const linkedinKeys = { all: () => [...prospectKeys.all(), 'linkedin'] as const };

export async function fetchLeadJourney(
  client: SupabaseClient,
  leadId: string,
): Promise<LeadJourney | null> {
  const { data, error } = await client
    .from('lead_linkedin_journey')
    .select(LEAD_COLUMNS)
    .eq('lead_id', leadId)
    .maybeSingle();
  if (error) throw new Error(error.message);
  return data;
}
export async function fetchDealJourney(
  client: SupabaseClient,
  dealId: string,
): Promise<DealJourney | null> {
  const { data, error } = await client
    .from('deal_linkedin_journey')
    .select(DEAL_COLUMNS)
    .eq('deal_id', dealId)
    .maybeSingle();
  if (error) throw new Error(error.message);
  return data;
}
export async function fetchLeadJourneyDeals(
  client: SupabaseClient,
  leadId: string,
): Promise<DealJourney[]> {
  const all: DealJourney[] = [];
  for (let page = 0; page < 100; page += 1) {
    const { data, error } = await client
      .from('deal_linkedin_journey')
      .select(DEAL_COLUMNS)
      .eq('lead_id', leadId)
      .order('deal_created_at', { ascending: false })
      .order('deal_id')
      .range(all.length, all.length + PROSPECT_PAGE - 1);
    if (error) throw new Error(error.message);
    if (data.length === 0) return all;
    all.push(...data);
  }
  throw new Error('Too many linked deals. Paging stopped.');
}
export async function fetchLinkedinScores(client: SupabaseClient) {
  const weekly: WeeklyScore[] = [];
  const messages: MessageScore[] = [];
  for (let page = 0; page < 100; page += 1) {
    const { data, error } = await client
      .from('linkedin_scoreboard_weekly')
      .select(WEEK_COLUMNS)
      .order('week_start', { ascending: false })
      .order('tier')
      .order('actor_id')
      .range(weekly.length, weekly.length + PROSPECT_PAGE - 1);
    if (error) throw new Error(error.message);
    if (data.length === 0) break;
    weekly.push(...data);
    if (page === 99) throw new Error('Too many scoreboard weeks. Paging stopped.');
  }
  for (let page = 0; page < 100; page += 1) {
    const { data, error } = await client
      .from('linkedin_scoreboard_by_message')
      .select(MESSAGE_COLUMNS)
      .order('tier')
      .order('copy_set_id')
      .range(messages.length, messages.length + PROSPECT_PAGE - 1);
    if (error) throw new Error(error.message);
    if (data.length === 0) break;
    messages.push(...data);
    if (page === 99) throw new Error('Too many scoreboard messages. Paging stopped.');
  }
  return { weekly, messages };
}
export function useLeadJourney(leadId: string) {
  const { client } = useAuth();
  return useQuery({
    queryKey: [...linkedinKeys.all(), 'lead', leadId],
    queryFn: () => fetchLeadJourney(client, leadId),
    retry: false,
  });
}
export function useDealJourney(dealId: string, revision = '') {
  const { client } = useAuth();
  return useQuery({
    queryKey: [...linkedinKeys.all(), 'deal', dealId, revision],
    queryFn: () => fetchDealJourney(client, dealId),
    retry: false,
  });
}
export function useLeadJourneyDeals(leadId: string, enabled: boolean, revision = 0) {
  const { client } = useAuth();
  return useQuery({
    queryKey: [...linkedinKeys.all(), 'lead-deals', leadId, revision],
    queryFn: () => fetchLeadJourneyDeals(client, leadId),
    enabled,
    retry: false,
  });
}
export function useLinkedinScores() {
  const { client } = useAuth();
  return useQuery({
    queryKey: [...linkedinKeys.all(), 'scores'],
    queryFn: () => fetchLinkedinScores(client),
    retry: false,
  });
}
export function useScoreboardPeople() {
  const { client } = useAuth();
  return useQuery({
    queryKey: [...linkedinKeys.all(), 'people'],
    retry: false,
    queryFn: async () => {
      const people: { id: string; display_name: string; email: string }[] = [];
      for (let page = 0; page < 100; page += 1) {
        const { data, error } = await client
          .from('profiles')
          .select('id, display_name, email')
          .order('id')
          .range(people.length, people.length + PROSPECT_PAGE - 1);
        if (error) throw new Error(error.message);
        if (data.length === 0) return new Map(people.map((p) => [p.id, p.display_name || p.email]));
        people.push(...data);
      }
      throw new Error('Too many people. Paging stopped.');
    },
  });
}
export function useRecordLeadMilestone() {
  const { client } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { leadId: string; kind: LeadMilestone; occurredOn?: string }) => {
      const { data, error } = await client.rpc('record_lead_milestone', {
        p_lead: input.leadId,
        p_kind: input.kind,
        ...(input.occurredOn ? { p_occurred_on: input.occurredOn } : {}),
      });
      if (error) throw new Error(error.message);
      return data;
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: linkedinKeys.all() });
    },
  });
}
export function leadMilestoneError(raw: string) {
  if (raw.includes('lead_not_from_linkedin')) return 'This lead did not start on LinkedIn.';
  if (raw.includes('unknown_milestone_kind')) return 'Choose Call held, No show, or Proposal sent.';
  return prospectErrorMessage(raw);
}

/** The existing per-lead Phase 2 reads, reused for Tier 1 and their drill IDs. */
export function useTier1JourneyEvidence(prospects: readonly Prospect[]) {
  const { client } = useAuth();
  const leadIds = [
    ...new Set(
      prospects
        .filter((p) => p.tier === 'tier1')
        .flatMap((p) => (p.promoted_lead_id === null ? [] : [p.promoted_lead_id])),
    ),
  ];
  const leads = useQueries({
    queries: leadIds.map((leadId) => ({
      queryKey: [...linkedinKeys.all(), 'lead', leadId],
      queryFn: () => fetchLeadJourney(client, leadId),
      retry: false,
    })),
  });
  const deals = useQueries({
    queries: leadIds.map((leadId) => ({
      queryKey: [...linkedinKeys.all(), 'lead-deals', leadId, 0],
      queryFn: () => fetchLeadJourneyDeals(client, leadId),
      retry: false,
    })),
  });
  const data: Tier1JourneyEvidence[] = leads.flatMap((query, index) => {
    const row = query.data;
    return row == null
      ? []
      : [
          {
            prospect_id: row.prospect_id,
            call_held_at: row.call_held_at,
            proposal_sent_at: row.proposal_sent_at,
            won: (deals[index]?.data ?? []).some(
              (deal) => deal.prospect_id === row.prospect_id && deal.outcome === 'won',
            ),
          },
        ];
  });
  return {
    data,
    isPending: [...leads, ...deals].some((q) => q.isPending),
    isError: [...leads, ...deals].some((q) => q.isError),
  };
}
